export type PageKind = 'search_results' | 'product_listing' | 'seller_page';

export type FieldDataType = 'string' | 'decimal' | 'url' | 'image' | 'string_list';

export interface RegistryField {
  key: string; // canonical key used in output JSON
  label: string; // human label for UI
  page_types: PageKind[];
  data_type: FieldDataType;
  is_array?: boolean;
  default_attribute?: string; // e.g., 'href' or 'src'
}

export const FIELD_REGISTRY: RegistryField[] = [
  // Common listing (search results)
  {
    key: 'title',
    label: 'Title',
    page_types: ['search_results', 'product_listing'],
    data_type: 'string',
  },
  {
    key: 'url',
    label: 'URL',
    page_types: ['search_results', 'product_listing'],
    data_type: 'url',
    default_attribute: 'href',
  },
  {
    key: 'price',
    label: 'Price',
    page_types: ['search_results', 'product_listing'],
    data_type: 'decimal',
  },
  // Single image is only used on search results; products should use images[]
  {
    key: 'image',
    label: 'Image',
    page_types: ['search_results'],
    data_type: 'image',
    default_attribute: 'src',
  },
  {
    key: 'images',
    label: 'Images',
    page_types: ['product_listing'],
    data_type: 'image',
    is_array: true,
    default_attribute: 'src',
  },
  {
    key: 'description',
    label: 'Description',
    page_types: ['product_listing'],
    data_type: 'string',
  },
  {
    key: 'availability',
    label: 'Availability/Stock',
    page_types: ['product_listing'],
    data_type: 'string',
  },
  {
    key: 'condition',
    label: 'Condition',
    page_types: ['search_results', 'product_listing'],
    data_type: 'string',
  },
  { key: 'brand', label: 'Brand', page_types: ['product_listing'], data_type: 'string' },
  {
    key: 'itemNumber',
    label: 'Item Number',
    // search_results is included because many marketplaces surface a stable
    // item-id directly in their search JSON (Taobao `nid`, Tmall `nid`,
    // Amazon `asin` in some response shapes). When we can capture it at
    // stage-1, downstream dedup keys on (marketplace_definition_id, item_id)
    // immediately instead of waiting for stage-2 product scrapes.
    page_types: ['search_results', 'product_listing'],
    data_type: 'string',
  },
  {
    key: 'relatedItems',
    label: 'Related items (variants)',
    page_types: ['product_listing'],
    data_type: 'string_list',
    is_array: true,
  },
  {
    key: 'number_sold',
    label: 'Number Sold',
    page_types: ['product_listing'],
    data_type: 'decimal',
  },
  {
    key: 'seller',
    label: 'Seller Name',
    page_types: ['search_results', 'product_listing', 'seller_page'],
    data_type: 'string',
  },
  {
    key: 'seller_country',
    label: 'Seller Country',
    page_types: ['product_listing', 'seller_page'],
    data_type: 'string',
  },
  {
    key: 'seller_location',
    label: 'Seller Location',
    page_types: ['product_listing'],
    data_type: 'string',
  },
  // Optional. seller_navigation still fills this when the field is absent.
  // Declare it when the store URL is on the product page itself (a link, or a
  // JSON island such as Qoo10's goods-consts-data.minishopURL).
  {
    key: 'seller_store_url',
    label: 'Seller Store URL',
    page_types: ['product_listing'],
    data_type: 'url',
    default_attribute: 'href',
  },
  // Seller page specific
  {
    key: 'seller_name',
    label: 'Seller Name (Page)',
    page_types: ['seller_page'],
    data_type: 'string',
  },
  {
    key: 'seller_rating',
    label: 'Seller Rating',
    page_types: ['seller_page'],
    data_type: 'decimal',
  },
  { key: 'followers', label: 'Followers', page_types: ['seller_page'], data_type: 'decimal' },
  { key: 'location', label: 'Location', page_types: ['seller_page'], data_type: 'string' },
  { key: 'description', label: 'Description', page_types: ['seller_page'], data_type: 'string' },
];

export function getRegistryFor(page: PageKind): RegistryField[] {
  return FIELD_REGISTRY.filter(f => f.page_types.includes(page));
}
